// Discovery rows for sections/sivana-pdp-recommendations.liquid: loads Shopify's recommendations
// and the shopper's recently viewed products as they scroll near a row, removes products already
// shown in a row above, and wires up the arrows.
const RECENT_KEY = 'sivana:recently-viewed';
const RECENT_MAX = 16;

function readRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((handle) => typeof handle === 'string');
  } catch (error) {
    return [];
  }
}

function rememberProduct(handle) {
  if (!handle || rememberProduct.done) return;
  rememberProduct.done = true;
  try {
    const list = [handle, ...readRecent().filter((item) => item !== handle)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch (error) {
    // Private browsing or storage blocked: the Recently Viewed row simply stays hidden.
  }
}

// Each product appears once, in the highest row that has it. Recently Viewed is left whole.
function removeRepeats() {
  const seen = new Set();
  document.querySelectorAll('sivana-recommendations').forEach((row) => {
    if (row.dataset.handle) seen.add(row.dataset.handle);
  });
  document.querySelectorAll('sivana-recommendations:not([data-source="recently_viewed"])').forEach((row) => {
    row.querySelectorAll('.sivana-recs__link').forEach((link) => {
      const handle = link.dataset.handle;
      if (seen.has(handle)) link.closest('.sivana-recs__card').remove();
      else seen.add(handle);
    });
    row.update?.();
  });
}

// Cards for products by handle, from sections/sivana-rec-card.liquid (follows renamed-product redirects).
async function fetchCards(handles) {
  const root = window.Shopify?.routes?.root || '/';
  const cards = await Promise.all(
    handles.map(async (handle) => {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=sivana-rec-card`);
        if (!response.ok) return null;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return html.querySelector('.sivana-recs__card');
      } catch (error) {
        return null;
      }
    })
  );
  return cards.filter(Boolean);
}

class SivanaRecommendations extends HTMLElement {
  connectedCallback() {
    rememberProduct(this.dataset.handle);
    if (this.dataset.source !== 'recently_viewed' && this.querySelector('.sivana-recs__card')) {
      this.setup();
      if (this.dataset.pairs) this.whenNear(() => this.loadPairs());
      return;
    }
    this.whenNear(() => {
      if (this.dataset.source === 'recently_viewed') this.loadRecent();
      else if (this.dataset.url) this.load();
    });
  }

  whenNear(callback) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        callback();
      },
      { rootMargin: '0px 0px 800px 0px' }
    );
    // A hidden element never intersects, so watch its section wrapper instead.
    observer.observe(this.parentElement || this);
  }

  // "Goes great with" products tagged by an old handle: fetch them and put them first.
  async loadPairs() {
    const handles = this.dataset.pairs.split(',').filter(Boolean);
    const cards = await fetchCards(handles);
    const track = this.querySelector('.sivana-recs__track');
    const limit = Number(this.dataset.limit) || 10;
    const fresh = cards.filter((card) => {
      const handle = card.querySelector('.sivana-recs__link')?.dataset.handle;
      return handle && handle !== this.dataset.handle && !track.querySelector(`[data-handle="${CSS.escape(handle)}"]`);
    });
    track.prepend(...fresh);
    while (track.children.length > limit) track.lastElementChild.remove();
    // The browser keeps the old first card in place, which would leave the new ones scrolled off to the left.
    track.scrollLeft = 0;
    removeRepeats();
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('sivana-recommendations');
      if (!fresh || !fresh.querySelector('.sivana-recs__card')) return;
      this.innerHTML = fresh.innerHTML;
      this.setup();
    } catch (error) {
      // Recommendations are a nice-to-have; leave the row empty if they can't load.
    }
  }

  async loadRecent() {
    const limit = Number(this.dataset.limit) || 10;
    const handles = readRecent().filter((handle) => handle !== this.dataset.handle).slice(0, limit);
    // The collection row (sections/sivana-recently-viewed.liquid) waits for a few viewed products
    if (!handles.length || handles.length < (Number(this.dataset.min) || 1)) return;
    const cards = await fetchCards(handles);
    const track = this.querySelector('.sivana-recs__track');
    cards.forEach((card) => track.append(card));
    if (!track.children.length) return;
    this.hidden = false;
    this.setup();
  }

  setup() {
    const track = this.querySelector('.sivana-recs__track');
    const arrows = [...this.querySelectorAll('.sivana-recs__arrow')];
    if (!track) return;
    this.update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      arrows.forEach((arrow) => {
        arrow.disabled = arrow.dataset.direction === '-1' ? track.scrollLeft <= 2 : track.scrollLeft >= max;
      });
    };
    arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const card = track.querySelector('.sivana-recs__card');
        const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap) : 300;
        const cards = Math.max(1, Math.floor(track.clientWidth / step) - 1);
        track.scrollBy({ left: Number(arrow.dataset.direction) * step * cards, behavior: 'smooth' });
      });
    });
    track.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update, { passive: true });
    removeRepeats();
    this.update();
  }
}

if (!customElements.get('sivana-recommendations')) customElements.define('sivana-recommendations', SivanaRecommendations);
